import React, { ComponentType, ReactNode, useState } from "react";
import { IMyDealAppProps, MyDealApp } from "@components";
import { notificationProvider, PortalProvider } from "@providers";
import {
  MantineProvider,
  Global,
  createEmotionCache,
  ColorSchemeProvider,
  ColorScheme,
} from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { ModalsProvider } from "@mantine/modals";

const modirCache = createEmotionCache({ key: "mydeal" });

export interface IMyDealAppWrapperProps {
  children: ReactNode;
}

export interface IMyDealMantineProps extends IMyDealAppProps {
  appWrapperComponent: ComponentType<IMyDealAppWrapperProps>;
}

export const MyDealMantine: React.FC<
  React.PropsWithChildren<IMyDealMantineProps>
> = ({ children, appWrapperComponent, ...rest }) => {
  const [colorScheme, setColorScheme] = useState<ColorScheme>("light");
  const toggleColorScheme = (value?: ColorScheme) =>
    setColorScheme(value || (colorScheme === "dark" ? "light" : "dark"));

  const queryClient = {
    defaultOptions: {
      mutations: {
        retry: (retryCount: any, error: any) => {
          if (error.statusCode === 200) {
            return false;
          }
          return true;
        },
      },
      queries: {
        retry: (retryCount: any, error: any) => {
          if (error.statusCode === 200) {
            return false;
          }
          return true;
        },
      },
    },
  };

  const Wrapper = appWrapperComponent;

  return (
    <ColorSchemeProvider
      colorScheme={colorScheme}
      toggleColorScheme={toggleColorScheme}
    >
      <MantineProvider
        emotionCache={modirCache}
        withGlobalStyles
        withNormalizeCSS
        theme={{
          colorScheme,
          colors: {
            brand: [
              "#E8F0FD",
              "#C6DBFA",
              "#A3C6F8",
              "#81B1F5",
              "#5E9BF2",
              "#3B86F0",
              "#1971ED",
              "#1566D8",
              "#125CC4",
              "#0E51AF",
            ],
            mydealDark: [
              "#F6F6F6",
              "#969696",
              "#5B5B5B",
              "#222222",
              "#444444",
              "#666666",
              "#888888",
              "#020202",
              "#010101",
              "#010101",
            ],
          },
          primaryColor: "brand",
        }}
      >
        <ModalsProvider>
          <Global
            styles={{
              html: { height: "100%" },
              body: { fontSize: 12, height: "100%" },
            }}
          />
          <Notifications position="top-right" />
          <Wrapper>
            <MyDealApp
              notificationProvider={notificationProvider}
              reactQueryClientConfig={queryClient}
              {...rest}
            >
              <PortalProvider>{children}</PortalProvider>
            </MyDealApp>
          </Wrapper>
        </ModalsProvider>
      </MantineProvider>
    </ColorSchemeProvider>
  );
};
